/**
 * MarkdownContent — Shared AI response renderer
 *
 * Handles:
 *   - Display math: \[...\] (MinerU) and $$...$$ blocks → KaTeX
 *   - Inline math: \(...\) and $...$ → KaTeX inline
 *   - Markdown tables: | col | col | → styled HTML table
 *   - Headings ##, ###
 *   - Bold **text**, italic *text* (inline in any line)
 *   - Bullet lists, numbered lists, horizontal rules
 *
 * Props:
 *   content  — raw markdown string from the LLM
 *   compact  — true for mini copilot modal (smaller font sizes)
 */

import katex from 'katex';
import 'katex/dist/katex.min.css';
import previewPaperFigureSource from '../../assets/preview-paper-figure.svg?url';

// ─── Block types ──────────────────────────────────────────────────────────────

type Block =
  | { kind: 'line'; text: string }
  | { kind: 'math'; latex: string }
  | { kind: 'table'; header: string[]; rows: string[][] };

// ─── Block parser ─────────────────────────────────────────────────────────────

function parseBlocks(content: string): Block[] {
  const lines = content.split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    // ── Display math: \[...\]  (MinerU default output) ───────────────────────
    if (trimmed === '\\[') {
      const latex: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== '\\]') {
        latex.push(lines[i]);
        i++;
      }
      blocks.push({ kind: 'math', latex: latex.join('\n') });
      i++; // skip closing \]
      continue;
    }

    // ── Display math: $$ block ────────────────────────────────────────────────
    if (trimmed === '$$') {
      const latex: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== '$$') {
        latex.push(lines[i]);
        i++;
      }
      blocks.push({ kind: 'math', latex: latex.join('\n') });
      i++; // skip closing $$
      continue;
    }

    // ── Display math: single-line $$ formula $$ ───────────────────────────────
    if (trimmed.startsWith('$$') && trimmed.endsWith('$$') && trimmed.length > 4) {
      blocks.push({ kind: 'math', latex: trimmed.slice(2, -2).trim() });
      i++;
      continue;
    }

    // ── Markdown table ────────────────────────────────────────────────────────
    if (trimmed.startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      // Separator rows: content between pipes is only -, :, spaces
      const parsedRows = tableLines
        .filter(l => !l.replace(/\|/g, '').trim().match(/^[-:\s]+$/))
        .map(l =>
          l.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim()),
        );
      if (parsedRows.length > 0) {
        const [header, ...rows] = parsedRows;
        blocks.push({ kind: 'table', header: header ?? [], rows });
      }
      continue;
    }

    blocks.push({ kind: 'line', text: raw });
    i++;
  }

  return blocks;
}

// ─── KaTeX helper ─────────────────────────────────────────────────────────────

function renderKatex(latex: string, display: boolean): string {
  try {
    return katex.renderToString(latex.trim(), {
      displayMode: display,
      throwOnError: false,
      output: 'html',
    });
  } catch {
    return latex;
  }
}

// ─── Inline content renderer ──────────────────────────────────────────────────
// Handles $math$, \(math\), **bold**, *italic* inside a single line of text.

function InlineText({ text }: { text: string }) {
  // Split on inline math patterns
  const mathRx = /(\$[^$\n]+\$|\\\([^)]*\\\))/g;
  const parts: Array<{ isMath: boolean; content: string }> = [];
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = mathRx.exec(text)) !== null) {
    if (m.index > last) parts.push({ isMath: false, content: text.slice(last, m.index) });
    parts.push({ isMath: true, content: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ isMath: false, content: text.slice(last) });
  if (parts.length === 0) parts.push({ isMath: false, content: text });

  return (
    <>
      {parts.map((part, pi) => {
        if (part.isMath) {
          const isParen = part.content.startsWith('\\(');
          const latex = isParen ? part.content.slice(2, -2) : part.content.slice(1, -1);
          return (
            <span
              key={pi}
              dangerouslySetInnerHTML={{ __html: renderKatex(latex, false) }}
            />
          );
        }

        // Bold + italic in plain text segments
        const fmtRx = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g;
        const segs: Array<{ bold?: boolean; italic?: boolean; t: string }> = [];
        let fl = 0;
        let fm: RegExpExecArray | null;
        while ((fm = fmtRx.exec(part.content)) !== null) {
          if (fm.index > fl) segs.push({ t: part.content.slice(fl, fm.index) });
          const isBold = fm[0].startsWith('**');
          segs.push(isBold
            ? { bold: true, t: fm[0].slice(2, -2) }
            : { italic: true, t: fm[0].slice(1, -1) });
          fl = fm.index + fm[0].length;
        }
        if (fl < part.content.length) segs.push({ t: part.content.slice(fl) });
        if (segs.length === 0) segs.push({ t: part.content });

        return (
          <span key={pi}>
            {segs.map((s, si) => {
              if (s.bold) return <strong key={si}>{s.t}</strong>;
              if (s.italic) return <em key={si}>{s.t}</em>;
              return <span key={si}>{s.t}</span>;
            })}
          </span>
        );
      })}
    </>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

interface MarkdownContentProps {
  content: string;
  /** Smaller font sizes for the mini copilot modal */
  compact?: boolean;
  /** Resolve only already-owned images; this renderer never fetches an asset. */
  resolvePaperAsset?: (assetId: string) => { src: string; alt: string } | null;
}

export function MarkdownContent({ content, compact = false, resolvePaperAsset }: MarkdownContentProps) {
  const blocks = parseBlocks(content);

  // Font size tokens
  const md  = compact ? 'text-sm'  : 'text-base';  // ## headings
  const sm  = compact ? 'text-xs'  : 'text-sm';    // ### headings, bold lines
  const xs  = 'text-xs';                            // body text (same in both modes)

  return (
    <div className="flex flex-col gap-0.5">
      {blocks.map((block, bi) => {

        // ── Display math block ──────────────────────────────────────────────
        if (block.kind === 'math') {
          return (
            <div
              key={bi}
              className="my-2 py-3 px-3 rounded-lg overflow-x-auto"
              style={{ background: 'var(--app-surface)' }}
              dangerouslySetInnerHTML={{ __html: renderKatex(block.latex, true) }}
            />
          );
        }

        // ── Table ───────────────────────────────────────────────────────────
        if (block.kind === 'table') {
          return (
            <div
              key={bi}
              className="my-2 overflow-x-auto rounded-lg border"
              style={{ borderColor: 'var(--app-border)' }}
            >
              <table className="w-full text-xs border-collapse">
                {block.header.length > 0 && (
                  <thead>
                    <tr style={{ background: 'var(--app-surface)' }}>
                      {block.header.map((h, hi) => (
                        <th
                          key={hi}
                          className="px-3 py-2 text-left border-b font-medium whitespace-nowrap"
                          style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
                        >
                          <InlineText text={h} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {block.rows.map((row, ri) => (
                    <tr
                      key={ri}
                      style={{ background: ri % 2 === 0 ? 'var(--app-bg)' : 'var(--app-surface)' }}
                    >
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className="px-3 py-1.5 border-b"
                          style={{ borderColor: 'var(--app-border)', color: 'var(--app-text-subtle)' }}
                        >
                          <InlineText text={cell} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        // ── Line ────────────────────────────────────────────────────────────
        const { text } = block;
        const t = text.trim();

        // The extractor rewrites local raster references into opaque digests.
        // Everything else stays escaped, inert text, including raw HTML images.
        const image = /^!\[([^\]\n]*)\]\(paper-asset:([0-9a-f]{64})\)$/.exec(t);
        if (image) {
          const resolved = resolvePaperAsset?.(image[2]);
          if (resolved && (/^blob:[^\s]+$/.test(resolved.src) || resolved.src === previewPaperFigureSource)) {
            return <img key={bi} src={resolved.src} alt={resolved.alt || image[1]} className="block w-full max-h-80 object-contain my-2" />;
          }
          return <p key={bi} className={xs} style={{ color: 'var(--app-text-muted)' }}>{image[1] || 'Extracted image'}</p>;
        }

        if (t.startsWith('## '))
          return (
            <p key={bi} className={`${md} mt-1 mb-1.5`} style={{ color: 'var(--app-text)' }}>
              <InlineText text={t.slice(3)} />
            </p>
          );

        if (t.startsWith('### '))
          return (
            <p key={bi} className={`${sm} mt-3 mb-1`} style={{ color: 'var(--app-text)', opacity: 0.9 }}>
              <InlineText text={t.slice(4)} />
            </p>
          );

        if (t.startsWith('**') && t.endsWith('**') && !t.slice(2, -2).includes('**'))
          return (
            <p key={bi} className={`${sm} mb-0.5`} style={{ color: 'var(--app-text)' }}>
              <InlineText text={t.slice(2, -2)} />
            </p>
          );

        if (t.startsWith('- '))
          return (
            <p key={bi} className={`${xs} pl-3 mb-0.5`} style={{ color: 'var(--app-text-muted)' }}>
              {'• '}<InlineText text={t.slice(2)} />
            </p>
          );

        if (t.startsWith('*') && !t.startsWith('**'))
          return (
            <p key={bi} className={`${xs} italic mt-2`} style={{ color: 'var(--app-text-muted)' }}>
              <InlineText text={t.replace(/^\*|\*$/g, '')} />
            </p>
          );

        if (t.startsWith('---'))
          return <hr key={bi} className="my-2" style={{ borderColor: 'var(--app-border)' }} />;

        if (t.match(/^\d+\./))
          return (
            <p key={bi} className={`${xs} pl-3 mb-0.5`} style={{ color: 'var(--app-text-muted)' }}>
              <InlineText text={t} />
            </p>
          );

        if (!t) return <div key={bi} className="h-1" />;

        return (
          <p key={bi} className={`${xs} mb-0.5`} style={{ color: 'var(--app-text-subtle)' }}>
            <InlineText text={t} />
          </p>
        );
      })}
    </div>
  );
}
